import { useMemo, useState } from "react";
import { ArrowUpDown, Search } from "lucide-react";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { monthLabel } from "../lib/csvAnalysis";
import { COLORS, fmtNum, slug } from "../lib/format";

const PAGE = 25;
const COLS = [["id", "User"], ["visits", "Visits"], ["qty", "Qty picked"], ["avg", "Avg qty / visit"], ["pph", "Picks / hr"], ["activeMonths", "Active months"], ["last", "Last active"]];

export const UserTable = ({ data, selected, onToggle, onOpen }) => {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(["visits", -1]);
  const [page, setPage] = useState(0);
  const rows = useMemo(() => {
    const f = q ? data.users.filter((u) => u.id.toLowerCase().includes(q.toLowerCase())) : data.users;
    const [k, d] = sort;
    return [...f].sort((a, b) => (a[k] > b[k] ? d : a[k] < b[k] ? -d : 0));
  }, [data.users, q, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const view = rows.slice(page * PAGE, page * PAGE + PAGE);
  const cols = COLS.filter(([k]) => (data.hasQty || (k !== "qty" && k !== "avg")) && (data.hasHop || k !== "pph"));
  return (
    <div className="panel" data-testid="user-table">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-slate-800">
        <h3 className="font-display text-lg font-semibold text-slate-100">All users <span className="font-mono text-sm text-slate-500">({fmtNum(rows.length)})</span></h3>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="user-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search users…" className="pl-9 bg-[#0B0F19] border-slate-700 text-slate-100" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">
            <th className="w-12" />
            {cols.map(([k, l]) => (
              <th key={k} className="text-left px-4 py-3">
                <button data-testid={`sort-${k}`} onClick={() => setSort([k, sort[0] === k ? -sort[1] : -1])} className={`inline-flex items-center gap-1 font-mono text-xs uppercase tracking-wider ${sort[0] === k ? "text-ep-soft" : "text-slate-500 hover:text-slate-300"}`}>{l}<ArrowUpDown className="h-3 w-3" /></button>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {view.map((u) => {
              const si = selected.indexOf(u.id);
              return (
                <tr key={u.id} data-testid={`user-table-row-${slug(u.id)}`} onClick={() => onOpen(u.id)} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-800/40 cursor-pointer transition-colors duration-150">
                  <td className="pl-5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox data-testid={`user-checkbox-${slug(u.id)}`} checked={si >= 0} onCheckedChange={() => onToggle(u.id)} style={si >= 0 ? { background: COLORS[si], borderColor: COLORS[si] } : undefined} className="border-slate-600" />
                  </td>
                  <td className="px-4 py-3 text-slate-100 font-medium whitespace-nowrap">{u.id}</td>
                  <td className="px-4 py-3 font-mono text-slate-300">{fmtNum(u.visits)}</td>
                  {data.hasQty && <td className="px-4 py-3 font-mono text-slate-300">{fmtNum(u.qty)}</td>}
                  {data.hasQty && <td className="px-4 py-3 font-mono text-slate-400">{fmtNum(u.avg)}</td>}
                  {data.hasHop && <td className="px-4 py-3 font-mono text-cyan-300" data-testid={`user-pph-${slug(u.id)}`}>{fmtNum(u.pph)}</td>}
                  <td className="px-4 py-3 font-mono text-slate-400">{u.activeMonths}</td>
                  <td className="px-4 py-3 font-mono text-slate-400 whitespace-nowrap">{monthLabel(u.last)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between p-4 border-t border-slate-800 text-xs text-slate-500 font-mono">
        <span data-testid="pagination-info">Page {page + 1} / {pages}</span>
        <div className="flex gap-2">
          <button data-testid="pagination-prev" disabled={page === 0} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800">Prev</button>
          <button data-testid="pagination-next" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800">Next</button>
        </div>
      </div>
    </div>
  );
};
